import React, { ChangeEvent, FC, useEffect, useState } from "react";
import { Field, Form, Formik } from "formik";
import { Row, Select } from "antd";
import LoadingOverlay from "../../../../../components/LoadingOverlay";
import { ActionButton, Button, Col, Container, Label } from "../../../styles";
import { FormValues, IFilter } from "../../../types";
import { ChargebackFilter } from "../../types";
import ChargebackService from "../../service/chargebackService";
import { can, PortalPermissions } from "../../../../../config/permissions";

export const FormFilter: FC<IFilter> = ({ getFilters, email }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [filters, setFilters] = useState<ChargebackFilter | undefined>();
    const serviceChargeback = new ChargebackService();
  
    const formatDate = (date: Date) => {
        const pad = (n: number) => n.toString().padStart(2, "0");

        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
            date.getDate()
        )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
            date.getSeconds()
        )}`;
    };

    const getInitialValue = (): ChargebackFilter => {
        const today = new Date();

        return {
            initDate: formatDate(
                new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0)
            ),
            finishDate: formatDate(
                new Date(
                    today.getFullYear(),
                    today.getMonth(),
                    today.getDate(),
                    23,
                    59,
                    59
                )
            ),
            brand: "",
            tipoPagamento: "",
            merchant: "",
            acquirer: undefined,
            acquirerNsu: "",
            businessName: "",
        };
    };

    const handleChangeInitialDate = (
        event: ChangeEvent<HTMLInputElement>,
        setFieldValue: any
    ) => {
        const [year, month, day] = event.target.value.split("-").map(Number);
        setFieldValue(
            "initDate",
            formatDate(new Date(year, month - 1, day, 0, 0, 0))
        );
    };
    
    const handleDownload = async () => {
        const response = await serviceChargeback.downloadChargebackPanelXls(filters, email);

        const blob = new Blob([response], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = "RelatorioChargeback.xlsx";
        link.click();

        window.URL.revokeObjectURL(url);
        };

    const handleChangeFinalDate = (
        event: ChangeEvent<HTMLInputElement>,
        setFieldValue: any
    ) => {
        const [year, month, day] = event.target.value.split("-").map(Number);
        setFieldValue(
            "finishDate",
            formatDate(new Date(year, month - 1, day, 23, 59, 59))
        );
    };

    const submitFilters = async (values: ChargebackFilter) => {
        const payload = {
            ...values,
            acquirer: values.acquirer ?? undefined,
            brand: values.brand || undefined,
        };
       
        getFilters(payload);
        setFilters(payload);
        return payload;
    };

    useEffect(() => {
        submitFilters(getInitialValue());
    }, []);

    return (
        <Container style={{ padding: 0 }}>
            <LoadingOverlay show={isLoading} relative />

            <Row style={{ marginBottom: 8 }}>
                <Label>Filtrar por:</Label>
            </Row>

            <Formik initialValues={getInitialValue()} onSubmit={submitFilters}>
                {({ setFieldValue, values }) => (
                    <Form>
                        <Row gutter={[16, 16]} align="middle">
                            <Col>                                
                                <input
                                    type="date"
                                    value={values.initDate?.slice(0, 10)}
                                    onChange={(e) =>
                                        handleChangeInitialDate(e, setFieldValue)
                                    }
                                />
                            </Col>

                            <Col>                              
                                <input
                                    type="date"
                                    value={values.finishDate?.slice(0, 10)}
                                    onChange={(e) =>
                                        handleChangeFinalDate(e, setFieldValue)
                                    }
                                />
                            </Col>

                            <Col>
                                <Select
                                    placeholder="Bandeira"
                                    allowClear
                                    value={values.brand || undefined}
                                    style={{ width: 180 }}
                                    options={[
                                        { value: "", label: " Bandeira" },
                                        { value: "VISA", label: "VISA" },
                                        { value: "MASTERCARD", label: "MASTERCARD" },
                                        { value: "ELO", label: "ELO" },
                                        { value: "AMEX", label: "AMEX" },
                                    ]}
                                    onChange={(value) =>
                                        setFieldValue("brand", value || "")
                                    }
                                />
                            </Col>

                            <Col>
                                <Select
                                    placeholder="Tipo pagamento"
                                    allowClear
                                    value={values.tipoPagamento || undefined}
                                    style={{ width: 200 }}
                                    options={[
                                        { value: "", label: "Tipo pagamento" },
                                        { value: "001", label: "Crédito à Vista" },
                                        { value: "002", label: "Parcelado s/ Juros" },
                                        { value: "003", label: "Parcelado c/ Juros" },
                                        { value: "005", label: "Débito" },                                       
                                    ]}
                                    onChange={(value) =>
                                        setFieldValue("tipoPagamento", value || "")
                                    }
                                />
                            </Col>

                            <Col style={{ marginLeft: 18 }}>
                                <Field name="merchant" placeholder="Cód. Lojista" />
                            </Col>

                            <Col>
                                <Field name="acquirerNsu" placeholder="NSU" />
                            </Col>

                            <Col>
                                <Field name="businessName" placeholder="Empresa" />
                            </Col>

                            <Col>
                                <Select
                                    placeholder="Adquirente"
                                    allowClear
                                    value={values.acquirer}
                                    style={{ width: 180 }}
                                    options={[
                                        { value: "", label: "Adquirente" },
                                        { value: 3, label: "Adiq" },
                                        { value: 10, label: "Rede" },
                                        { value: 99, label: "Valori" },
                                    ]}
                                    onChange={(value) =>
                                        setFieldValue("acquirer", value)
                                    }
                                />
                            </Col>

                            <Col>
                                <ActionButton htmlType="submit" type="primary">
                                    CONSULTAR
                                </ActionButton>
                            </Col>
                             <Col>
                              {can(PortalPermissions.cobrancas.chargebacks.exportar) && (
                                <Button
                              onClick={handleDownload}
                              style={{                               
                                  marginRight: "30px",
                                  width: "11rem"                              
                              }}
                              >
                              Download xlsx
                              </Button>
                              )}
                            </Col>
                          
                        </Row>
                    </Form>
                )}
            </Formik>
        </Container>
    );
};
